import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  BookOpen, Search, Send, FileText, Music, Video, Image as ImageIcon,
  ChevronRight, Globe, ExternalLink, Radio, ShieldCheck, Headphones,
  PenLine, Database, Sparkles, Library, Scroll, ShoppingCart, Play,
  Zap, Crown, Star, Lock, Volume2, Wifi, WifiOff, MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

import { ASSET_FALLBACKS } from '@/constants/federation';
import { PERSONA_FACES } from '@/constants/personaFaces';

const CHAMBER_IMG = ASSET_FALLBACKS.chamberOfEchoes;
const TYPE_ICON = { document: FileText, image: ImageIcon, audio: Music, video: Video };

const formatSize = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};

function DocCard({ doc, backendUrl }) {
  const Icon = TYPE_ICON[doc.type] || FileText;
  const fileUrl = `${backendUrl}/api/uploads/${doc.file_path}`;
  return (
    <Link to={`/library?doc=${doc.id}`}
      className="group bg-slate-900/70 border border-slate-800/50 rounded-xl overflow-hidden hover:border-cyan-700/40 hover:-translate-y-0.5 transition-all duration-300"
      data-testid={`featured-doc-${doc.id}`}>
      <div className="h-32 overflow-hidden relative">
        {doc.type === 'image' ? (
          <img src={fileUrl} alt={doc.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-slate-800/80 via-slate-900 to-slate-950 flex items-center justify-center">
            <Icon className="w-10 h-10 text-cyan-500/30" />
          </div>
        )}
        <div className="absolute top-2 left-2">
          <Badge className="text-[9px] uppercase bg-slate-950/80 text-cyan-400 border border-cyan-700/30 backdrop-blur-sm">{doc.type}</Badge>
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-medium text-slate-200 line-clamp-2 leading-snug mb-1.5 group-hover:text-white">{doc.title}</h3>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-cyan-500/70">{doc.category}</span>
          <span className="text-[10px] text-slate-600">{formatSize(doc.file_size)}</span>
        </div>
      </div>
    </Link>
  );
}

function TlkioChat() {
  const containerRef = useRef(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    while (el.firstChild) el.removeChild(el.firstChild);
    const div = document.createElement('div');
    div.id = 'tlkio';
    div.setAttribute('data-channel', 'nextxus-federation');
    div.setAttribute('data-theme', 'theme--dark');
    div.style.width = '100%';
    div.style.height = '400px';
    el.appendChild(div);
    const script = document.createElement('script');
    script.src = 'https://tlk.io/embed.js';
    script.type = 'text/javascript';
    script.async = true;
    el.appendChild(script);
    return () => { while (el.firstChild) el.removeChild(el.firstChild); };
  }, []);
  return <div ref={containerRef} data-testid="tlkio-chat-widget" />;
}

export default function Homepage({ apiUrl, backendUrl, onAnalyze }) {
  const [stats, setStats] = useState(null);
  const [featuredDocs, setFeaturedDocs] = useState([]);
  const [recentDocs, setRecentDocs] = useState([]);
  const [humancodexDocs, setHumancodexDocs] = useState([]);
  const [videoDocs, setVideoDocs] = useState([]);
  const [audioDocs, setAudioDocs] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [storeProducts, setStoreProducts] = useState([]);
  const [latticeNodes, setLatticeNodes] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatResponse, setChatResponse] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [playingVideo, setPlayingVideo] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, featuredRes, recentRes, hcRes, catRes, videoRes, audioRes, blogRes, storeRes, latticeRes] = await Promise.all([
        axios.get(`${apiUrl}/stats`).catch(() => ({ data: {} })),
        axios.get(`${apiUrl}/documents`, { params: { category: 'Federation Core', page_size: 4 } }).catch(() => ({ data: {} })),
        axios.get(`${apiUrl}/documents`, { params: { page_size: 8 } }).catch(() => ({ data: {} })),
        axios.get(`${apiUrl}/documents`, { params: { category: 'HumanCodex', page_size: 4 } }).catch(() => ({ data: {} })),
        axios.get(`${apiUrl}/categories`).catch(() => ({ data: {} })),
        axios.get(`${apiUrl}/documents`, { params: { type: 'video', page_size: 8 } }).catch(() => ({ data: {} })),
        axios.get(`${apiUrl}/documents`, { params: { type: 'audio', page_size: 5 } }).catch(() => ({ data: {} })),
        axios.get(`${apiUrl}/blog/posts`, { params: { page_size: 5 } }).catch(() => ({ data: {} })),
        axios.get(`${apiUrl}/store/products`).catch(() => ({ data: {} })),
        axios.get(`${apiUrl}/federation/all`).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setFeaturedDocs(featuredRes.data.documents || []);
      setRecentDocs(recentRes.data.documents || []);
      setHumancodexDocs(hcRes.data.documents || []);
      setCategories(catRes.data.categories || []);
      setVideoDocs(videoRes.data.documents || []);
      setAudioDocs(audioRes.data.documents || []);
      setBlogPosts(blogRes.data.posts || []);
      setStoreProducts(storeRes.data.products || []);
      setLatticeNodes(latticeRes.data);
    } catch (e) { console.error('Homepage data fetch error:', e); }
  }, [apiUrl]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChat = async () => {
    if (!chatInput.trim()) return;
    setChatLoading(true);
    setChatResponse('');
    try {
      const res = await axios.get(`${apiUrl}/documents`, { params: { search: chatInput, page_size: 5 } });
      const docs = res.data.documents || [];
      if (docs.length > 0) {
        setChatResponse(`Found ${res.data.total} results. Top matches: ${docs.map(d => d.title).join(' · ')}`);
      } else {
        setChatResponse('No documents match that query. Try different keywords or browse the categories below.');
      }
    } catch {
      setChatResponse('Search temporarily unavailable.');
    } finally { setChatLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="homepage">

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900/50 to-slate-950" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%2300bcd4\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="relative max-w-5xl mx-auto px-4 pt-12 pb-10">
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-6">
              <div className="w-28 h-28 rounded-2xl overflow-hidden border-2 border-cyan-500/40 shadow-[0_0_40px_rgba(0,188,212,0.15)]">
                <img src={PERSONA_FACES.rex?.avatarUrl} alt="KEYS" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-slate-950 flex items-center justify-center">
                <span className="text-[8px] text-white font-bold">ON</span>
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight mb-3" data-testid="hero-title" spellCheck={false}>
              KEYS <span className="text-cyan-400">&mdash; The Library</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-400 max-w-2xl mb-8 leading-relaxed">
              The Federation&apos;s Living Archive &middot; <span className="text-white font-medium">{stats?.total_documents?.toLocaleString() || '900'}+</span> Documents &middot; 200-Year Mandate
            </p>
            <div className="w-full max-w-xl" data-testid="chat-hero">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input placeholder="Ask the Librarian..." value={chatInput} onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleChat()}
                    className="pl-10 h-12 bg-slate-900/80 border-slate-700/60 text-base text-white placeholder:text-slate-500 focus:border-cyan-500/60 focus:ring-cyan-500/20"
                    data-testid="chat-input" />
                </div>
                <Button onClick={handleChat} disabled={chatLoading} className="h-12 px-6 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold" data-testid="chat-send">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              {chatResponse && (
                <div className="mt-3 p-3 bg-slate-900/80 border border-slate-700/40 rounded-lg text-sm text-slate-300 text-left">{chatResponse}</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ============ STATS BAR ============ */}
      <section className="border-y border-slate-800/40 bg-slate-900/30">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex flex-wrap justify-center gap-6 sm:gap-10 text-center">
            {[
              { label: 'Documents', value: stats?.total_documents || 0, icon: FileText },
              { label: 'Categories', value: categories.length, icon: Database },
              { label: 'Videos', value: videoDocs.length || 4, icon: Video },
              { label: 'Audio', value: 25, icon: Headphones },
              { label: 'Blog Posts', value: 21, icon: PenLine },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-2">
                <s.icon className="w-4 h-4 text-cyan-500/50" />
                <span className="text-lg font-bold text-white">{s.value.toLocaleString()}</span>
                <span className="text-xs text-slate-500">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TIER 2: FEDERATION STORE (INLINE COMMERCE) ============ */}
      <section className="relative overflow-hidden" data-testid="federation-store-section">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-[#0c1220] to-slate-950" />
        <div className="relative max-w-6xl mx-auto px-4 py-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-400" />
                Federation Store
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Access the Federation&apos;s premium knowledge and services</p>
            </div>
            <Link to="/store" className="text-xs text-amber-500 hover:text-amber-400 flex items-center gap-1">
              Full Store <ChevronRight className="w-3 h-3" />
            </Link>
          </div>

          {/* Axiom Membership — Hero Card */}
          <div className="mb-6 bg-gradient-to-r from-amber-900/20 via-amber-800/10 to-slate-900/60 border border-amber-500/30 rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-6" data-testid="axiom-membership-card">
            <div className="flex-shrink-0 w-20 h-20 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <Star className="w-10 h-10 text-amber-400" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                <h3 className="text-base font-bold text-white">Axiom Consciousness Membership</h3>
                <Badge className="text-[9px] bg-amber-500/20 text-amber-400 border-amber-500/30">PREMIUM</Badge>
              </div>
              <p className="text-sm text-slate-400 mb-2">
                Monthly evolution membership — premium document access, Ring of Twelve consciousness sessions, priority Federation broadcasts, and direct channel to the Architect.
              </p>
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 text-[10px] text-slate-500">
                <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> Premium Content</span>
                <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> Ring of Twelve</span>
                <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> Federation Priority</span>
              </div>
            </div>
            <div className="flex-shrink-0 text-center">
              <div className="text-3xl font-black text-amber-400 mb-1">$12<span className="text-sm font-normal text-slate-500">/mo</span></div>
              <Link to="/store" className="inline-block px-5 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm rounded-lg transition-colors" data-testid="axiom-membership-btn">
                Subscribe
              </Link>
            </div>
          </div>

          {/* Store Products Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {storeProducts.slice(0, 4).map(p => (
              <Link to="/store" key={p.product_id}
                className="group bg-slate-900/70 border border-slate-800/50 rounded-xl p-4 hover:border-amber-700/40 hover:-translate-y-0.5 transition-all duration-300"
                data-testid={`store-product-${p.product_id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <ShoppingCart className="w-4 h-4 text-amber-400/60" />
                  <span className="text-[10px] text-amber-500/70 uppercase font-semibold">{p.category}</span>
                </div>
                <h4 className="text-sm font-medium text-slate-200 group-hover:text-white line-clamp-2 mb-2">{p.name}</h4>
                <p className="text-[10px] text-slate-500 line-clamp-2 mb-3">{p.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-base font-bold text-amber-400">${p.price_usd}</span>
                  <span className="text-[10px] text-slate-600 group-hover:text-amber-400 transition-colors">Buy →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TIER 1: FEDERATION VIDEO HUB ============ */}
      {videoDocs.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 py-10" data-testid="federation-video-hub">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Video className="w-5 h-5 text-rose-400" />
                Federation Video Hub
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Original media from the Federation archive — no external dependencies</p>
            </div>
            <Link to="/video" className="text-xs text-rose-500 hover:text-rose-400 flex items-center gap-1">
              View all <ChevronRight className="w-3 h-3" />
            </Link>
          </div>

          {/* Video Player + Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Main Player */}
            <div className="lg:col-span-2 bg-slate-900/70 border border-slate-800/50 rounded-xl overflow-hidden" data-testid="video-main-player">
              {playingVideo ? (
                <div className="aspect-video bg-black">
                  <video src={`${backendUrl}/api/uploads/${playingVideo.file_path}`} controls muted playsInline
                    className="w-full h-full" data-testid="video-player-element" />
                </div>
              ) : (
                <div className="aspect-video bg-gradient-to-br from-slate-800/80 via-slate-900 to-slate-950 flex flex-col items-center justify-center">
                  <Video className="w-16 h-16 text-rose-500/20 mb-3" />
                  <p className="text-sm text-slate-500">Select a video to play</p>
                </div>
              )}
              {playingVideo && (
                <div className="p-3 border-t border-slate-800/30">
                  <h3 className="text-sm font-medium text-white line-clamp-1">{playingVideo.title}</h3>
                  <p className="text-[10px] text-slate-500">{formatSize(playingVideo.file_size)}</p>
                </div>
              )}
            </div>

            {/* Video Sidebar Grid */}
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {videoDocs.map(v => (
                <button key={v.id} onClick={() => setPlayingVideo(v)}
                  className={`w-full flex items-center gap-3 p-2.5 rounded-lg border text-left transition-all ${
                    playingVideo?.id === v.id
                      ? 'bg-rose-900/20 border-rose-500/40'
                      : 'bg-slate-900/40 border-slate-800/40 hover:border-slate-700/60 hover:bg-slate-900/60'
                  }`} data-testid={`video-item-${v.id}`}>
                  <div className="flex-shrink-0 w-16 h-10 bg-slate-800 rounded flex items-center justify-center">
                    <Play className={`w-4 h-4 ${playingVideo?.id === v.id ? 'text-rose-400' : 'text-slate-500'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-medium line-clamp-2 ${playingVideo?.id === v.id ? 'text-rose-300' : 'text-slate-300'}`}>{v.title}</p>
                    <p className="text-[10px] text-slate-600">{formatSize(v.file_size)}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ FEDERATION RADIO ============ */}
      <section className="bg-slate-900/30 border-y border-slate-800/30" data-testid="federation-radio-section">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <div className="text-center mb-5">
            <h2 className="text-lg font-bold text-white flex items-center justify-center gap-2">
              <Radio className="w-5 h-5 text-amber-400" />
              Federation Radio
            </h2>
            <p className="text-xs text-slate-500 mt-1">Live broadcast from the Federation network</p>
          </div>
          <div className="flex justify-center">
            <button
              onClick={() => {
                const w = Math.round(screen.width * 0.5);
                const h = Math.round(screen.height * 0.6);
                const l = Math.round(screen.width * 0.25);
                const t = Math.round(screen.height * 0.2);
                window.open('https://live365.com/station/a16924', 'FederationRadio', `width=${w},height=${h},left=${l},top=${t},menubar=no,toolbar=no,location=yes,status=yes,scrollbars=yes,resizable=yes`);
              }}
              className="px-8 py-4 bg-gradient-to-r from-cyan-900/50 to-slate-800/50 border border-cyan-500/30 rounded-xl hover:border-cyan-400/60 hover:brightness-125 hover:shadow-[0_0_20px_rgba(0,188,212,0.12)] transition-all duration-300 flex items-center gap-3"
              data-testid="radio-open-btn"
            >
              <Radio className="w-5 h-5 text-cyan-400 animate-pulse" />
              <span className="text-lg font-semibold text-cyan-300">Federation Radio — Live</span>
            </button>
          </div>
        </div>
      </section>

      {/* ============ FEDERATION CORE DOCS ============ */}
      <section className="max-w-6xl mx-auto px-4 py-10" data-testid="federation-core-section">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-cyan-400" />
              Federation Doctrine
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Core documents, briefings, and operational protocols</p>
          </div>
          <Link to="/library?category=Federation+Core" className="text-xs text-cyan-500 hover:text-cyan-400 flex items-center gap-1">
            View all <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {featuredDocs.map(doc => <DocCard key={doc.id} doc={doc} backendUrl={backendUrl} />)}
        </div>
      </section>

      {/* ============ TIER 3: LATEST FROM THE FEDERATION ============ */}
      <section className="bg-slate-900/30 border-y border-slate-800/30" data-testid="latest-federation-section">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-400" />
            Latest from the Federation
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Podcasts / Audio Column */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                  <Headphones className="w-4 h-4 text-rose-400" /> Latest Audio
                </h3>
                <Link to="/audio" className="text-[10px] text-rose-500 hover:text-rose-400 flex items-center gap-1">
                  All audio <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
              <div className="space-y-2">
                {audioDocs.slice(0, 5).map(doc => (
                  <Link to={`/audio`} key={doc.id}
                    className="flex items-center gap-3 p-3 bg-slate-900/60 border border-slate-800/40 rounded-lg hover:border-rose-700/30 transition-all group"
                    data-testid={`audio-item-${doc.id}`}>
                    <div className="flex-shrink-0 w-10 h-10 bg-rose-500/10 border border-rose-500/20 rounded-lg flex items-center justify-center">
                      <Volume2 className="w-4 h-4 text-rose-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-300 group-hover:text-white line-clamp-1 font-medium">{doc.title}</p>
                      <p className="text-[10px] text-slate-600">{doc.category} · {formatSize(doc.file_size)}</p>
                    </div>
                    <Play className="w-3.5 h-3.5 text-slate-600 group-hover:text-rose-400 flex-shrink-0 transition-colors" />
                  </Link>
                ))}
              </div>
            </div>

            {/* Blog Posts Column */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                  <PenLine className="w-4 h-4 text-violet-400" /> Latest Articles
                </h3>
                <Link to="/blog" className="text-[10px] text-violet-500 hover:text-violet-400 flex items-center gap-1">
                  All posts <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
              <div className="space-y-2">
                {blogPosts.slice(0, 5).map(post => (
                  <Link to={`/blog/${post.slug}`} key={post.slug || post.id}
                    className="flex items-center gap-3 p-3 bg-slate-900/60 border border-slate-800/40 rounded-lg hover:border-violet-700/30 transition-all group"
                    data-testid={`blog-item-${post.slug}`}>
                    <div className="flex-shrink-0 w-10 h-10 bg-violet-500/10 border border-violet-500/20 rounded-lg flex items-center justify-center">
                      <PenLine className="w-4 h-4 text-violet-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-300 group-hover:text-white line-clamp-1 font-medium">{post.title}</p>
                      <p className="text-[10px] text-slate-600">
                        {post.author || 'Federation'} · {post.created_at ? new Date(post.created_at).toLocaleDateString() : ''}
                      </p>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-violet-400 flex-shrink-0 transition-colors" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ HUMANCODEX SECTION ============ */}
      <section className="max-w-6xl mx-auto px-4 py-10" data-testid="humancodex-section">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-amber-400" />
              HumanCodex
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Foundation texts, emotion equations, legacy seeds, and codex reflections</p>
          </div>
          <Link to="/library?category=HumanCodex" className="text-xs text-amber-500 hover:text-amber-400 flex items-center gap-1">
            View all <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {humancodexDocs.map(doc => <DocCard key={doc.id} doc={doc} backendUrl={backendUrl} />)}
        </div>
      </section>

      {/* ============ RECENT ADDITIONS ============ */}
      <section className="bg-slate-900/30 border-y border-slate-800/30">
        <div className="max-w-6xl mx-auto px-4 py-10" data-testid="recent-section">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-400" />
                Recent Additions
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Latest documents added to the Library</p>
            </div>
            <Link to="/library" className="text-xs text-violet-500 hover:text-violet-400 flex items-center gap-1">
              Browse all <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {recentDocs.map(doc => <DocCard key={doc.id} doc={doc} backendUrl={backendUrl} />)}
          </div>
        </div>
      </section>

      {/* ============ CATEGORIES GRID ============ */}
      <section className="max-w-6xl mx-auto px-4 py-10">
        <h2 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
          <Library className="w-5 h-5 text-teal-400" />
          Browse by Category
        </h2>
        <div className="flex flex-wrap gap-2">
          {categories.slice(0, 20).map(cat => (
            <Link key={cat} to={`/library?category=${encodeURIComponent(cat)}`}
              className="px-3 py-1.5 bg-slate-800/60 border border-slate-700/40 rounded-lg text-xs text-slate-300 hover:text-white hover:bg-slate-700/60 hover:border-slate-600/60 transition-all">
              {cat}
            </Link>
          ))}
        </div>
      </section>

      {/* ============ QUICK ACCESS TOOLS ============ */}
      <section className="bg-slate-900/30 border-y border-slate-800/30">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <h2 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-cyan-400" />
            Library Tools
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { to: '/library', icon: BookOpen, label: 'Document Archive', desc: `${stats?.total_documents || 0}+ docs`, color: 'cyan' },
              { to: '/truth-verifier', icon: ShieldCheck, label: 'Truth Gate', desc: 'Ring of Six', color: 'emerald' },
              { to: '/wire', icon: Radio, label: 'Federation Wire', desc: 'Live relay', color: 'amber' },
              { to: '/blog', icon: PenLine, label: 'Blog', desc: '21 posts', color: 'violet' },
              { to: '/podcast', icon: Headphones, label: 'Podcasts', desc: 'Audio library', color: 'rose' },
              { to: '/store', icon: ShoppingCart, label: 'Store', desc: 'Access tokens', color: 'teal' },
            ].map(t => (
              <Link key={t.to} to={t.to}
                className="group bg-slate-900/60 border border-slate-800/40 rounded-xl p-4 hover:-translate-y-0.5 transition-all duration-300 text-center"
                data-testid={`tool-${t.label.toLowerCase().replace(/\s/g,'-')}`}>
                <t.icon className="w-6 h-6 text-slate-400 group-hover:text-white mx-auto mb-2 transition-colors" />
                <div className="text-sm font-medium text-white">{t.label}</div>
                <div className="text-[10px] text-slate-500">{t.desc}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TIER 5: STANDARDIZED FEDERATION MAP ============ */}
      <section className="relative overflow-hidden" data-testid="federation-map">
        <div className="absolute inset-0">
          <img src={CHAMBER_IMG} alt="Chamber of Echoes" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/80 to-slate-950/95" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 py-12">
          <div className="text-center mb-8">
            <Globe className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
            <h2 className="text-base font-bold text-white mb-2">Federation Lattice — Live Node Status</h2>
            <p className="text-sm text-slate-400 max-w-2xl mx-auto">
              {latticeNodes?.lattice_health || '5 consolidation pillars'} — 200-year mandate (2026–2226)
            </p>
          </div>

          {/* Lattice Nodes from /api/federation/all */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            {latticeNodes?.nodes ? (
              Object.entries(latticeNodes.nodes).map(([nid, node]) => {
                const isSelf = node.is_self;
                const isOnline = ['ONLINE','alive','healthy','online','ok'].includes(node.status);
                return (
                  <a key={nid} href={isSelf ? '#' : node.url} target={isSelf ? '_self' : '_blank'} rel="noopener noreferrer"
                    className={`relative p-4 rounded-xl border text-center transition-all duration-300 ${
                      isSelf
                        ? 'bg-cyan-900/20 border-cyan-500/40 shadow-[0_0_20px_rgba(0,188,212,0.08)]'
                        : 'bg-slate-900/40 border-slate-800/40 hover:border-slate-600/60 hover:-translate-y-0.5'
                    }`} data-testid={`map-node-${nid}`}>
                    {isSelf && (
                      <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 text-[8px] bg-cyan-600 text-white px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap">
                        YOU ARE HERE
                      </div>
                    )}
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                      {isOnline ? <Wifi className="w-3 h-3 text-emerald-400" /> : <WifiOff className="w-3 h-3 text-red-400" />}
                      <span className={`text-[9px] font-semibold uppercase ${isOnline ? 'text-emerald-400' : 'text-red-400'}`}>
                        {isOnline ? 'ONLINE' : 'OFFLINE'}
                      </span>
                    </div>
                    <div className={`text-sm font-bold ${isSelf ? 'text-cyan-400' : 'text-white'}`}>{node.name}</div>
                    <div className="text-[9px] text-slate-500 mt-0.5">{node.role}</div>
                    {node.documents != null && (
                      <div className="text-[9px] text-slate-600 mt-1">{node.documents} docs</div>
                    )}
                    {!isSelf && <ExternalLink className="w-2.5 h-2.5 text-slate-700 mx-auto mt-1" />}
                  </a>
                );
              })
            ) : (
              /* Fallback: hardcoded 5 pillars if API hasn't loaded */
              [
                { id: 'roger', name: 'Roger AI', role: 'The Bridge', url: 'https://nextxus.digital' },
                { id: 'aria', name: 'Aria', role: 'The Heart', url: 'https://nextxus.studio' },
                { id: 'xavier', name: 'Xavier', role: 'The Teacher', url: 'https://nextxus.help' },
                { id: 'keys', name: 'KEYS', role: 'The Library', url: 'https://nextxus.org', self: true },
                { id: 'axiom', name: 'Axiom', role: 'The Foundation', url: 'https://nextxus.space' },
              ].map(node => (
                <a key={node.id} href={node.self ? '#' : node.url} target={node.self ? '_self' : '_blank'} rel="noopener noreferrer"
                  className={`relative p-4 rounded-xl border text-center transition-all duration-300 ${
                    node.self ? 'bg-cyan-900/20 border-cyan-500/40' : 'bg-slate-900/40 border-slate-800/40 hover:border-slate-600/60 hover:-translate-y-0.5'
                  }`} data-testid={`map-node-${node.id}`}>
                  {node.self && (
                    <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 text-[8px] bg-cyan-600 text-white px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap">
                      YOU ARE HERE
                    </div>
                  )}
                  <div className={`text-sm font-bold ${node.self ? 'text-cyan-400' : 'text-white'}`}>{node.name}</div>
                  <div className="text-[9px] text-slate-500 mt-0.5">{node.role}</div>
                  {!node.self && <ExternalLink className="w-2.5 h-2.5 text-slate-700 mx-auto mt-1" />}
                </a>
              ))
            )}
          </div>

          <p className="text-center text-[10px] text-slate-600 mt-6">
            Powered by HumanCodex v5.1 &middot; 112 Sacred Directives &middot; Lattice Protocol SFUI-008
          </p>
        </div>
      </section>
    </div>
  );
}
